import React, { useEffect, useState } from 'react';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { useOrderContext } from '../../../../components/OrderProvider';
import { useSnackbar } from 'notistack';
import { FormattedMessage } from 'react-intl';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Grid from '@material-ui/core/Grid';
import OrderTagsForm from './OrderTagsForm';
import ColoredChip from '@afosto/app-components-react/ColoredChip';
import FormDrawer from '@afosto/app-components-react/FormDrawer';
import { orders as ordersApi } from '@afosto/order/api/Orders';
import { profile as profileApi } from '@afosto/iam/api/Profile';
import translations from './translations';

const ORDER_TAGS_PATH = 'afosto.tenant.odr.orders.tags';

const OrderTagsSection = () => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [showEditTags, setShowEditTags] = useState(false);
  const [formattedOrderTags, setFormattedOrderTags] = useState([]);

  const { order, setOrder } = useOrderContext();
  const { tags, isLocked } = order || {};

  useEffect(() => {
    let isMounted = true;
    const handleFetchProfile = async () => {
      try {
        const { data = [] } = await profileApi.getProfile(ORDER_TAGS_PATH);

        const formattedData = data
          .filter(tag => tags.includes(tag?.code))
          .reduce((acc, tag) => [...acc, { ...tag, value: tag?.code }], []);
        if (isMounted) {
          setFormattedOrderTags(formattedData);
        }
      } catch (error) {
        if (isMounted) {
          setFormattedOrderTags([]);
        }
      }
    };

    if (isMounted && tags?.length > 0) {
      handleFetchProfile();
    } else {
      setFormattedOrderTags(tags);
    }

    return () => {
      isMounted = false;
    };
  }, [tags]);

  const handleChangeOrderTags = async orderTagsData => {
    try {
      const newTags = (orderTagsData?.tags ?? []).reduce((acc, tag) => [...acc, tag?.code], []);
      await ordersApi.updateOrderTags(order?.id, newTags);

      setOrder({
        ...order,
        tags: newTags,
      });
      setShowEditTags(false);
      enqueueSnackbar(intl.formatMessage(translations.feedback.orderTagsUpdated), {
        variant: 'success',
      });
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.orderTagsNotUpdated), {
        variant: 'error',
      });
    }
  };

  return (
    <>
      <Card>
        <CardHeader
          title={intl.formatMessage(translations.title)}
          titleTypographyProps={{ variant: 'h6' }}
          action={
            !isLocked && (
              <Button color="primary" variant="outlined" onClick={() => setShowEditTags(true)}>
                {intl.formatMessage(translations.actions.edit.label)}
              </Button>
            )
          }
        />
        <CardContent>
          {formattedOrderTags?.length > 0 && (
            <Grid container spacing={1}>
              {(formattedOrderTags ?? []).map(tag => (
                <Grid item key={tag?.code}>
                  <ColoredChip
                    label={tag?.label ?? tag}
                    colorCode={tag?.color ?? '#eee'}
                    size="small"
                  />
                </Grid>
              ))}
            </Grid>
          )}

          {!formattedOrderTags?.length && <FormattedMessage {...translations.emptyMessage} />}
        </CardContent>
      </Card>
      <FormDrawer
        title={intl.formatMessage(translations.orderTagsSettings.title)}
        isOpen={showEditTags}
        onClose={() => setShowEditTags(false)}
        drawerWidth="400px"
      >
        <OrderTagsForm
          tags={formattedOrderTags}
          onSubmit={handleChangeOrderTags}
          onCancel={() => setShowEditTags(false)}
        />
      </FormDrawer>
    </>
  );
};

export default OrderTagsSection;
